import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

const AMBIENT_PARTICLE_KEYS = [0, 1, 2, 3, 4] as const;

export interface WorkspaceAmbientBackgroundProps {
  /** Optional space color (hex) for a very subtle center glow */
  accentColor?: string | null;
  className?: string;
}

/**
 * Lightweight full-viewport workspace backdrop (CSS + few motion dots).
 * Replaces always-on D3 for shell performance; respects prefers-reduced-motion.
 */
export default function WorkspaceAmbientBackground({
  accentColor,
  className = '',
}: WorkspaceAmbientBackgroundProps) {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = () => setReducedMotion(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const accentGlow =
    accentColor && /^#[0-9A-Fa-f]{6}$/.test(accentColor)
      ? `radial-gradient(ellipse at center, ${accentColor}14 0%, transparent 65%)`
      : undefined;

  return (
    <div
      className={`fixed inset-0 pointer-events-none z-0 overflow-hidden ${className}`}
      aria-hidden
    >
      <div className="absolute inset-0 bg-gradient-to-br from-workspace-surface-50 via-workspace-surface-primary/30 to-workspace-surface-secondary/40" />
      {accentGlow ? (
        <div className="absolute inset-0" style={{ background: accentGlow }} />
      ) : null}

      {!reducedMotion && (
        <div className="absolute inset-0 opacity-[0.12]">
          {AMBIENT_PARTICLE_KEYS.map((i) => (
            <motion.div
              key={i}
              className="absolute h-1 w-1 rounded-full bg-workspace-primary-400/80"
              style={{
                left: `${12 + i * 18}%`,
                top: `${20 + (i % 3) * 22}%`,
              }}
              animate={{
                y: [0, -12, 0],
                opacity: [0.25, 0.55, 0.25],
              }}
              transition={{
                duration: 4 + i * 0.4,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: i * 0.5,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
